import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const { isAuthenticated, logout, user } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = getCartCount();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsAccountOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setIsAccountOpen(false);
    setIsMenuOpen(false);
    navigate("/");
  };

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${sectionId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 400);
    } else {
      const el = document.querySelector(`[data-testid="${sectionId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_tank-fashion/artifacts/rut5gr1k_igor-final.png" 
              alt="3T Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading text-2xl md:text-3xl tracking-tighter">3T</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/shop"
              className="link-underline text-sm uppercase tracking-widest font-semibold"
              data-testid="nav-shop-link"
            >
              Shop
            </Link>
            <button
              onClick={() => scrollToSection("lifestyle-section")}
              className="link-underline text-sm uppercase tracking-widest font-semibold bg-transparent border-none cursor-pointer text-white"
              data-testid="nav-philosophy-link"
            >
              Our Philosophy
            </button>
            <button
              onClick={() => scrollToSection("footer")}
              className="link-underline text-sm uppercase tracking-widest font-semibold bg-transparent border-none cursor-pointer text-white"
              data-testid="nav-contact-link"
            >
              Contact
            </button>

            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-2 text-[#FFD700] font-semibold"
                data-testid="nav-admin-link"
              >
                <Shield className="w-5 h-5" />
                Admin
              </Link>
            )}

            {/* Cart Icon */}
            <Link
              to="/cart"
              className="relative"
              data-testid="nav-cart-link"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FFD700] text-black text-xs flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Account Icon + Dropdown */}
            <div className="relative" ref={accountRef}>
              <button
                onClick={() => setIsAccountOpen(!isAccountOpen)}
                className="flex items-center bg-transparent border-none cursor-pointer text-white hover:text-[#FFD700] transition-colors"
                data-testid="nav-account-btn"
              >
                <User className="w-5 h-5" />
              </button>

              <AnimatePresence>
                {isAccountOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-10 w-48 bg-[#111] border border-white/10 shadow-xl"
                    data-testid="account-dropdown"
                  >
                    {isAuthenticated ? (
                      <>
                        <Link
                          to="/profile"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-account-link"
                        >
                          My Account
                        </Link>
                        <Link
                          to="/orders"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-orders-link"
                        >
                          Orders
                        </Link>
                        <Link
                          to="/wishlist"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-wishlist-link"
                        >
                          Wishlist
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-5 py-3 text-sm uppercase tracking-wider font-semibold text-[#FFD700] hover:bg-white/5 transition-colors border-t border-white/10 bg-transparent cursor-pointer"
                          data-testid="dropdown-logout-btn"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/auth"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-login-link"
                        >
                          Login
                        </Link>
                        <Link
                          to="/orders"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-orders-link"
                        >
                          Orders
                        </Link>
                        <Link
                          to="/wishlist"
                          className="block px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 hover:text-[#FFD700] transition-colors"
                          data-testid="dropdown-wishlist-link"
                        >
                          Wishlist
                        </Link>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/cart" className="relative" data-testid="mobile-cart-icon">
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FFD700] text-black text-xs flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="nav-mobile-menu-btn"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/10"
          >
            <div className="px-6 py-8 space-y-6">
              <Link
                to="/shop"
                className="block text-lg font-semibold uppercase tracking-widest"
                onClick={() => setIsMenuOpen(false)}
                data-testid="mobile-nav-shop"
              >
                Shop
              </Link>
              <button
                onClick={() => scrollToSection("lifestyle-section")}
                className="block text-lg font-semibold uppercase tracking-widest bg-transparent border-none cursor-pointer text-white text-left"
                data-testid="mobile-nav-philosophy"
              >
                Our Philosophy
              </button>
              <button
                onClick={() => scrollToSection("footer")}
                className="block text-lg font-semibold uppercase tracking-widest bg-transparent border-none cursor-pointer text-white text-left"
                data-testid="mobile-nav-contact"
              >
                Contact
              </button>

              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center gap-3 text-lg font-semibold uppercase tracking-widest text-[#FFD700]"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="mobile-nav-admin"
                >
                  <Shield className="w-5 h-5" />
                  Admin Panel
                </Link>
              )}

              <div className="border-t border-white/10 pt-6 space-y-6">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 text-lg font-semibold uppercase tracking-widest"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-nav-profile"
                    >
                      <User className="w-5 h-5" />
                      My Account
                    </Link>
                    <Link
                      to="/orders"
                      className="block text-lg font-semibold uppercase tracking-widest"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-nav-orders"
                    >
                      Orders
                    </Link>
                    <Link
                      to="/wishlist"
                      className="block text-lg font-semibold uppercase tracking-widest"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-nav-wishlist"
                    >
                      Wishlist
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="text-lg font-semibold uppercase tracking-widest text-[#FFD700] bg-transparent border-none cursor-pointer"
                      data-testid="mobile-nav-logout"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/auth"
                      className="flex items-center gap-3 text-lg font-semibold uppercase tracking-widest"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-nav-login"
                    >
                      <User className="w-5 h-5" />
                      Login
                    </Link>
                    <Link
                      to="/wishlist"
                      className="block text-lg font-semibold uppercase tracking-widest"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-nav-wishlist"
                    >
                      Wishlist
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
